import React, { useState } from 'react';

import { cssMap } from '@atlaskit/css';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ActionButtons } from './components/ActionButtons';
import { Avatar } from './components/Avatar';
import { UserContent } from './components/UserContent';

const NoopComponent = () => null;

const styles = cssMap({
	root: {
		position: 'relative',
		borderRadius: token('radius.large'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		backgroundColor: token('elevation.surface.sunken'),

		'&:hover': {
			backgroundColor: token('elevation.surface.raised.hovered'),
		},
	},
});

type UserInlinePreviewData = {
	accountId: string;
	name: string;
	avatarUrl?: string | null;
	jobTitle?: string | null;
	thirdPartyIntegrations?: {
		externalId: string;
		integrationId: string;
		product: string;
	}[];
};

interface UserInlinePreviewProps {
	data: UserInlinePreviewData;
	testId?: string;
	cloudId: string;
}

/**
 * Compact inline user preview with avatar, name, job title, and hover-revealed actions.
 *
 * @internal Not ready for consumption. Do not use in production.
 */
export const UserInlinePreview = componentWithFG(
	'pt_user_inline_preview_enabled',
	NoopComponent,
	UserInlinePreviewComponent,
);

function UserInlinePreviewComponent({
	data,
	testId = 'user-inline-preview',
	cloudId,
}: UserInlinePreviewProps) {
	const { accountId, name, avatarUrl, jobTitle, thirdPartyIntegrations } = data;
	const [isHovered, setIsHovered] = useState(false);

	return (
		<>
			<Box
				xcss={styles.root}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				testId={testId}
			>
				<Inline space="space.150" alignBlock="center">
					<Avatar src={avatarUrl} name={name} size="medium" />
					<UserContent name={name} jobTitle={jobTitle} />
					<ActionButtons
						name={name}
						accountId={accountId}
						isVisible={isHovered}
						cloudId={cloudId}
						thirdPartyIntegrations={thirdPartyIntegrations}
					/>
				</Inline>
			</Box>
		</>
	);
}
